import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { formatDate } from '../../utils/formatters.js';

/**
 * Rückfrage vor einer Entscheidung über einen Ferienantrag.
 *
 * Vorher liefen Genehmigen und Ablehnen als sofortiger Klick ohne Rückfrage,
 * und eine Ablehnung liess sich gar nicht begründen - der Mitarbeiter bekam
 * nur ein nacktes "abgelehnt". Bei einer Ablehnung ist die Begründung deshalb
 * Pflicht, bei Genehmigung und Stornierung freiwillig.
 *
 * @param {{decision: 'APPROVE'|'REJECT'|'CANCEL', request: object}|null} pending
 */
export function LeaveDecisionModal({ pending, onClose, onConfirm, isPending }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');

  useEffect(() => {
    setNote('');
  }, [pending?.request?.id, pending?.decision]);

  if (!pending) {
    return null;
  }

  const { decision, request } = pending;
  const noteRequired = decision === 'REJECT';
  const canConfirm = !isPending && (!noteRequired || note.trim().length > 0);

  const titles = {
    APPROVE: t('hr.leave.decide.approve.title'),
    REJECT: t('hr.leave.decide.reject.title'),
    CANCEL: t('hr.leave.decide.cancel.title'),
  };

  return (
    <Modal
      open
      title={titles[decision]}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={decision === 'APPROVE' ? 'primary' : 'danger'}
            disabled={!canConfirm}
            onClick={() => onConfirm(note.trim() || null)}
          >
            {titles[decision]}
          </Button>
        </>
      }
    >
      <FieldGrid
        items={[
          { label: t('hr.leave.col.employee'), value: request.employeeName },
          { label: t('hr.leave.col.type'), value: request.leaveTypeName },
          { label: t('hr.leave.col.start'), value: formatDate(request.startDate) },
          { label: t('hr.leave.col.end'), value: formatDate(request.endDate) },
          { label: t('hr.leave.col.days'), value: String(request.days) },
        ]}
      />

      <label className="field">
        <span className="field-label">
          {noteRequired ? t('hr.leave.decide.noteRequired') : t('hr.leave.decide.noteOptional')}
        </span>
        <textarea
          className="input"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          autoFocus
        />
      </label>
    </Modal>
  );
}
